import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, INK, JOB_SETTINGS_EDIT } from "../../../../../theme/tokens";
import type { JobEditRow } from "./useJobsEditState";

type Props = {
	job: JobEditRow;
	/** The employee's other jobs, which can become the new primary. */
	otherJobs: JobEditRow[];
	employeeName: string;
	onCancel: () => void;
	onRemove: () => void;
	onMuteInstead: () => void;
	onSetPrimaryAndRemove: (newPrimaryId: string) => void;
};

const bodySx = {
	display: "flex",
	flexDirection: "column",
	gap: 1,
	maxWidth: JOB_SETTINGS_EDIT.CONFIRM_BODY_MAX_WIDTH_PX,
	color: INK.SECONDARY,
	fontSize: FONT_SIZE.EMPHASIS,
} as const;

/**
 * Confirms removing a job. Schedulers often only want quiet, so "Mute instead" comes first.
 * It doesn't mention upcoming shifts because saving asks about them. Removing the primary job picks a new one.
 */
export const RemoveJobConfirmDialog: FC<Props> = ({ job, otherJobs, employeeName, onCancel, onRemove, onMuteInstead, onSetPrimaryAndRemove }) => {
	const { t } = useTranslation();
	const firstOtherId = otherJobs[0]?.id;
	// DEFAULT: pre-select the first other job so one click confirms; the list marks muted jobs, and the one picked is turned on as the new primary.
	const [newPrimaryId, setNewPrimaryId] = useState(firstOtherId);

	// Reset the choice when a different job is queued. Keyed on ids because the caller rebuilds the array each render.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `job.id` is the reset trigger, not a value read here.
	useEffect(() => setNewPrimaryId(firstOtherId), [job.id, firstOtherId]);

	if (job.isPrimary) {
		return (
			<SystemDialog
				open
				dataTestId="remove-primary-job-dialog"
				onClose={onCancel}
				title={t("jobNotifications.profile.removePrimary.title")}
				body={
					<Box sx={bodySx}>
						<Typography sx={{ fontSize: "inherit" }}>
							{t("jobNotifications.profile.removePrimary.body", { job: job.displayName, name: employeeName })}
						</Typography>
						<RadioGroup
							// DEFAULT: "" means nothing chosen, which only happens when there's no other job.
							value={newPrimaryId ?? ""}
							onChange={(event) => setNewPrimaryId(event.target.value)}
							aria-label={t("jobNotifications.profile.removePrimary.pick")}
						>
							{otherJobs.map((other) => (
								<FormControlLabel
									key={other.id}
									value={other.id}
									control={<Radio size="small" data-testid={`remove-primary-job-dialog-option-${other.id}`} />}
									label={other.isSilent ? t("jobNotifications.profile.removePrimary.optionMuted", { job: other.displayName }) : other.displayName}
									sx={{ color: other.isSilent ? INK.MUTED : INK.SECONDARY }}
								/>
							))}
						</RadioGroup>
					</Box>
				}
				cancel={t("cancel")}
				confirm={t("jobNotifications.profile.removePrimary.confirm")}
				confirmButtonVariant="primary"
				confirmDisabled={!newPrimaryId}
				onConfirm={() => newPrimaryId && onSetPrimaryAndRemove(newPrimaryId)}
			/>
		);
	}

	return (
		<SystemDialog
			open
			dataTestId="remove-job-dialog"
			onClose={onCancel}
			title={t("jobNotifications.profile.remove.title", { job: job.displayName })}
			body={
				<Box sx={bodySx}>
					<Typography sx={{ fontSize: "inherit" }}>{t("jobNotifications.profile.remove.body", { job: job.displayName, name: employeeName })}</Typography>
					{!job.isSilent && (
						<Typography sx={{ fontSize: "inherit", color: INK.MUTED }}>{t("jobNotifications.profile.remove.muteHint", { name: employeeName })}</Typography>
					)}
				</Box>
			}
			actionsStart={
				!job.isSilent && (
					<SystemButton
						buttonVariant="secondary"
						noFullWidthMobile
						onClick={onMuteInstead}
						sx={{ fontSize: FONT_SIZE.EMPHASIS }}
						data-testid="remove-job-dialog-mute-instead"
					>
						{t("jobNotifications.profile.remove.muteInstead")}
					</SystemButton>
				)
			}
			cancel={t("cancel")}
			confirm={t("jobNotifications.profile.remove.confirm")}
			confirmButtonVariant="delete"
			onConfirm={onRemove}
		/>
	);
};
